import { useEffect, useState } from 'react'
import { Search as SearchIcon, Truck, X } from 'lucide-react'
import type { BoxTruckDriverLastLocation } from '@numeo/types-core'
import { Button } from '@/components/ds2'
import { MultiLocationInput } from 'features/load-hub/components/filters/MultiLocationInput'
import { LocationData } from 'features/load-hub/types/filters'
import { NearbyDriversTab } from './NearbyDriversTab'

/** Default search radius (miles) — atrek's Search Vehicles default. */
const DEFAULT_RADIUS = '150'

/** Origin + radius the shown results were searched with — committed on Search, so editing the form after a
 *  search doesn't refetch until Search is pressed again. */
interface Committed {
    origin: { lat: number; lng: number }
    radiusMi: number
}

/**
 * "Search Vehicles" drawer (atrek's dispatch feature): the operator enters a point (city or zip) + a radius,
 * presses Search, and gets THEIR box-truck drivers sitting near it — the EXACT same table the load drawer's
 * Nearby Drivers tab renders (per-row contact / SMS / availability / note, verified badge, distance,
 * last-seen), just driven by a manually-picked origin instead of a load.
 */
export const SearchVehiclesDrawer = ({
    open,
    onClose,
    onDriverLocationSaved,
}: {
    open: boolean
    onClose: () => void
    /** A location corrected from THIS table. It renders the same editable rows as the load drawer over the
     *  same driver pool, so a correction made here has to reach the host's persisted driver selection too —
     *  otherwise the load drawer's pinned row is still rebuilt from the pre-edit fix. */
    onDriverLocationSaved?: (userId: string, lastLocation: BoxTruckDriverLastLocation) => void
}) => {
    // The picked origin — only a city/zip pick carries coordinates to search around (a bare state doesn't).
    const [pick, setPick] = useState<LocationData | null>(null)
    const [radius, setRadius] = useState(DEFAULT_RADIUS)
    // What the RESULTS below are searched with — only changes on Search, so the table doesn't refetch per keystroke.
    const [committed, setCommitted] = useState<Committed | null>(null)
    // Bumped on EVERY Search press. Feeds the results table's `key`, so pressing Search re-runs the fetch (and
    // resets the table's own filter) even when the origin + radius are unchanged — refreshing stale results and
    // recovering from a failed fetch, neither of which a same-value `committed` object would trigger on its own.
    const [runId, setRunId] = useState(0)

    // Reset everything when the drawer closes so a re-open starts clean.
    useEffect(() => {
        if (!open) {
            setPick(null)
            setRadius(DEFAULT_RADIUS)
            setCommitted(null)
        }
    }, [open])

    // Esc closes the drawer (it overlays the whole board) — but NOT while a child modal (SMS composer, note
    // editor, route map) is open: those are ds2 Modals listening on `document`, and this listener is on `window`,
    // so one press would bubble to both and the close would also wipe the search. ds2 Modals lock body scroll
    // while open, so `overflow: hidden` is the "a modal is up — let it take the Escape" signal.
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && document.body.style.overflow !== 'hidden') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, onClose])

    const lat = pick?.latitude ?? null
    const lng = pick?.longitude ?? null
    const radiusMi = parseFloat(radius)
    const canSearch = lat != null && lng != null && !isNaN(radiusMi) && radiusMi > 0

    const runSearch = () => {
        if (!canSearch || lat == null || lng == null) return
        // The server clamps the radius to [1, 500]; clamp here too and reflect it back into the field so the
        // results and the "No drivers within N mi" empty-state describe the radius actually searched, not a
        // larger number the operator typed that was never queried.
        const clamped = Math.min(500, Math.max(1, radiusMi))
        if (clamped !== radiusMi) setRadius(String(clamped))
        setCommitted({ origin: { lat, lng }, radiusMi: clamped })
        setRunId((n) => n + 1)
    }

    if (!open) return null

    return (
        <>
            <div className="fixed inset-0 z-[9996] bg-black/40" onClick={onClose} aria-hidden />
            <div className="fixed inset-y-0 right-0 z-[9997] flex w-full max-w-none flex-col border-l border-border bg-surface shadow-xl min-[900px]:w-[1240px] min-[900px]:max-w-[96vw]">
                <div className="flex items-center gap-3 border-b border-border px-3 py-4 min-[900px]:px-5">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent">
                        <Truck size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h2 className="text-base font-semibold leading-tight text-foreground">Search Vehicles</h2>
                        <p className="text-xs text-foreground-muted">Your box-truck drivers sitting near a point</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="rounded-lg p-1.5 text-foreground-muted transition-colors hover:bg-default hover:text-foreground"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="border-b border-border bg-background/60 px-3 py-4 min-[900px]:px-5">
                    <div className="grid grid-cols-2 items-end gap-3 min-[900px]:flex min-[900px]:flex-wrap">
                        <div className="col-span-2 min-w-0 min-[900px]:max-w-lg min-[900px]:flex-1">
                            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
                                Address <span className="text-danger">*</span>
                            </div>
                            {/* Same autocomplete as the board's Origin — city or zip; kept to ONE pick (the last),
                                since the search needs a single point. A state-only pick has no coordinates. */}
                            <MultiLocationInput
                                values={pick ? [pick] : []}
                                onChange={(next) => setPick(next.length ? next[next.length - 1] : null)}
                                placeholder="City or zip to find drivers around"
                                includeZones={false}
                            />
                        </div>
                        <div className="min-w-0">
                            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
                                Radius <span className="text-danger">*</span>
                            </div>
                            <div className="relative">
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={radius}
                                    onChange={(e) => {
                                        const v = e.target.value
                                        if (v === '' || /^\d*\.?\d*$/.test(v)) setRadius(v)
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') runSearch()
                                    }}
                                    aria-label="Search radius in miles"
                                    className="h-10 w-full rounded-lg border border-border bg-surface pl-3 pr-9 text-sm tabular-nums text-foreground shadow-field outline-none focus:border-accent min-[900px]:w-28"
                                />
                                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-foreground-muted">
                                    mi
                                </span>
                            </div>
                        </div>
                        <Button
                            variant="primary"
                            size="md"
                            disabled={!canSearch}
                            onClick={runSearch}
                            className="w-full min-[900px]:min-w-[110px] min-[900px]:w-auto"
                        >
                            <SearchIcon size={14} /> Search
                        </Button>
                    </div>
                    {pick && lat == null && <p className="mt-2 text-[11px] text-danger">Pick a city or zip — a state alone has no point to search around.</p>}
                </div>

                {/* The load drawer's Nearby Drivers table, driven by the committed origin + radius. It owns the
                    per-row contact / SMS / availability / note actions; the radius is controlled here so its
                    built-in dropdown is hidden. */}
                <div className="flex min-h-0 flex-1 flex-col overflow-auto px-3 py-4 min-[900px]:px-6">
                    {committed ? (
                        // Remount per Search press (runId) so a re-search refetches and the table's own driver
                        // filter + SMS-sent counts reset instead of surviving an origin change.
                        <NearbyDriversTab
                            key={`nearby-${runId}`}
                            origin={committed.origin}
                            radiusMi={committed.radiusMi}
                            title="Vehicles"
                            onDriverLocationSaved={onDriverLocationSaved}
                        />
                    ) : (
                        <div className="flex h-full flex-col items-center justify-center gap-3 px-8 pb-16 text-center">
                            <div className="flex size-14 items-center justify-center rounded-2xl bg-accent-soft text-accent">
                                <Truck size={26} />
                            </div>
                            <div className="text-sm font-semibold text-foreground">Find your drivers near a point</div>
                            <p className="max-w-sm text-xs leading-relaxed text-foreground-muted">
                                Enter a city or zip and a radius, then press Search — you&apos;ll get your box-truck drivers whose last known location is
                                nearby, in the same table as a load&apos;s Nearby Drivers.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}
